import { FormEvent, useEffect, useRef, useState } from "react";
import voiceReplyManifest from "./voice-replies.json";

type TranscriptLine = {
  role: "caller" | "agent";
  text: string;
};

type Subscription = {
  subscribed: boolean;
  status?: string;
  email?: string;
  test_mode?: boolean;
};

type VoiceReply = {
  id: string;
  text: string;
};

type StallAnalytics = {
  sessionId: string;
  attribution: Record<string, string>;
  track: (eventName: string, extra?: Record<string, string>) => void;
};

type VoiceReplyManifest = Record<
  "opening" | "generic" | "gift_card" | "computer" | "financial" | "authority" | "crypto" | "urgent",
  VoiceReply[]
>;

const voiceReplies = voiceReplyManifest as VoiceReplyManifest;

const apiBase = import.meta.env.VITE_API_BASE_URL ?? "";

type RecognitionResultLike = {
  0: { transcript: string };
  isFinal: boolean;
};

type RecognitionEventLike = {
  results: ArrayLike<RecognitionResultLike>;
  resultIndex: number;
};

type RecognitionLike = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((event: RecognitionEventLike) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
};

declare global {
  interface Window {
    SpeechRecognition?: new () => RecognitionLike;
    webkitSpeechRecognition?: new () => RecognitionLike;
    stallAnalytics?: StallAnalytics;
  }
}

const callerPrompts = [
  "This is the security department. We noticed a suspicious charge on your account.",
  "I need you to verify the card number so I can reverse it.",
  "Please open your computer and download our secure support application.",
  "This is urgent. If we do not fix it now, your account will be closed.",
  "Can you purchase a gift card to confirm your identity?",
];

function chooseReply(group: keyof VoiceReplyManifest, turn: number) {
  const choices = voiceReplies[group];
  return choices[turn % choices.length];
}

function replyFor(text: string, turn: number): VoiceReply {
  const normalized = text.toLowerCase();

  if (/gift card|voucher|target card|apple card/.test(normalized)) {
    return chooseReply("gift_card", turn);
  }
  if (/computer|download|install|anydesk|teamviewer/.test(normalized)) {
    return chooseReply("computer", turn);
  }
  if (/bank|account|card|charge|refund/.test(normalized)) {
    return chooseReply("financial", turn);
  }
  if (/social security|medicare|government|police|warrant|tax/.test(normalized)) {
    return chooseReply("authority", turn);
  }
  if (/crypto|bitcoin|wallet/.test(normalized)) {
    return chooseReply("crypto", turn);
  }
  if (/urgent|immediately|right now|today/.test(normalized)) {
    return chooseReply("urgent", turn);
  }

  return chooseReply("generic", turn);
}

function formatTime(seconds: number) {
  const minutes = Math.floor(seconds / 60).toString().padStart(2, "0");
  const remainder = (seconds % 60).toString().padStart(2, "0");
  return `${minutes}:${remainder}`;
}

export default function App() {
  const voiceSupported = Boolean(
    typeof window !== "undefined"
    && (window.SpeechRecognition || window.webkitSpeechRecognition)
    && typeof window.Audio !== "undefined",
  );
  const [running, setRunning] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [turns, setTurns] = useState(0);
  const [status, setStatus] = useState("Ready on this device");
  const [interim, setInterim] = useState("");
  const [email, setEmail] = useState("");
  const [billingMessage, setBillingMessage] = useState("");
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreCode, setRestoreCode] = useState("");
  const [restoreStep, setRestoreStep] = useState<"request" | "verify">("request");
  const [subscription, setSubscription] = useState<Subscription>({ subscribed: false });
  const [transcript, setTranscript] = useState<TranscriptLine[]>([
    { role: "agent", text: "Press Start, put the suspicious caller on speaker, and set this device nearby." },
  ]);

  const recognitionRef = useRef<RecognitionLike | null>(null);
  const recognitionActiveRef = useRef(false);
  const speakingRef = useRef(false);
  const processingRef = useRef(false);
  const microphoneBlockedRef = useRef(false);
  const restartTimerRef = useRef<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const microphoneStreamRef = useRef<MediaStream | null>(null);
  const runningRef = useRef(false);
  const turnsRef = useRef(0);
  const firstAgentReplyTrackedRef = useRef(false);
  const pricingViewTrackedRef = useRef(false);

  useEffect(() => {
    runningRef.current = running;
  }, [running]);

  useEffect(() => {
    const pricing = document.getElementById("pricing");
    if (!pricing || !window.IntersectionObserver) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting) && !pricingViewTrackedRef.current) {
          pricingViewTrackedRef.current = true;
          window.stallAnalytics?.track("pricing_view");
          observer.disconnect();
        }
      },
      { threshold: 0.35 },
    );
    observer.observe(pricing);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    turnsRef.current = turns;
  }, [turns]);

  useEffect(() => {
    if (!running) return;
    const timer = window.setInterval(() => setSeconds((value) => value + 1), 1000);
    return () => window.clearInterval(timer);
  }, [running]);

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const sessionId = query.get("session_id");

    async function refreshBilling() {
      try {
        const endpoint = sessionId
          ? `${apiBase}/api/billing/session?session_id=${encodeURIComponent(sessionId)}`
          : `${apiBase}/api/billing/me`;
        const response = await fetch(endpoint, { credentials: "include" });
        if (!response.ok) return;
        const data = await response.json() as Subscription;
        setSubscription(data);
        if (sessionId) {
          setBillingMessage(data.subscribed ? "Membership active. Stall the Scam is ready on this device." : "Checkout is still processing.");
          window.history.replaceState({}, "", `${window.location.pathname}#pricing`);
        }
      } catch {
        // The voice demo remains usable when billing is temporarily unavailable.
      }
    }

    void refreshBilling();
  }, []);

  useEffect(() => {
    for (const reply of voiceReplies.opening) {
      const audio = new Audio(`/audio/ruth-generative/${reply.id}.mp3`);
      audio.preload = "auto";
    }
    return () => {
      if (restartTimerRef.current !== null) window.clearTimeout(restartTimerRef.current);
      recognitionRef.current?.abort();
      audioRef.current?.pause();
      microphoneStreamRef.current?.getTracks().forEach((track) => track.stop());
      window.speechSynthesis?.cancel();
    };
  }, []);

  useEffect(() => {
    if (running && !subscription.subscribed && seconds >= 60) {
      stopSession();
      setStatus("Free tryout complete. Join for unlimited sessions.");
      window.location.hash = "pricing";
    }
  }, [running, seconds, subscription.subscribed]);

  function scheduleListening(delay = 500) {
    if (restartTimerRef.current !== null) window.clearTimeout(restartTimerRef.current);
    restartTimerRef.current = window.setTimeout(() => {
      restartTimerRef.current = null;
      beginListening();
    }, delay);
  }

  function beginListening() {
    if (
      !runningRef.current
      || recognitionActiveRef.current
      || speakingRef.current
      || processingRef.current
      || microphoneBlockedRef.current
    ) return;
    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!Recognition) {
      setStatus("Voice listening is not supported here. Use the demo caller button instead.");
      return;
    }

    const recognition = new Recognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";
    recognitionRef.current = recognition;

    recognition.onresult = (event) => {
      let finalText = "";
      let liveText = "";

      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const result = event.results[index];
        if (result.isFinal) finalText += result[0].transcript;
        else liveText += result[0].transcript;
      }

      setInterim(liveText);
      if (finalText.trim()) {
        processingRef.current = true;
        recognition.stop();
        handleCaller(finalText.trim());
      }
    };

    recognition.onerror = (event) => {
      if (event.error === "not-allowed") {
        microphoneBlockedRef.current = true;
        runningRef.current = false;
        setRunning(false);
        microphoneStreamRef.current?.getTracks().forEach((track) => track.stop());
        microphoneStreamRef.current = null;
        setStatus("Microphone access is blocked. Allow it, or use the demo caller button.");
      } else if (event.error !== "no-speech" && event.error !== "aborted") {
        setStatus("I missed that. Reconnecting the microphone…");
      }
    };

    recognition.onend = () => {
      recognitionActiveRef.current = false;
      if (
        runningRef.current
        && !speakingRef.current
        && !processingRef.current
        && !microphoneBlockedRef.current
      ) {
        // Chrome can end a continuous session after an internal timeout. A
        // measured retry keeps the microphone stable instead of rapid-cycling.
        scheduleListening(900);
      }
    };

    try {
      recognitionActiveRef.current = true;
      recognition.start();
      setStatus("Listening to the caller…");
    } catch {
      recognitionActiveRef.current = false;
      setStatus("Reconnecting the microphone…");
      scheduleListening(1200);
    }
  }

  function finishSpeaking() {
    speakingRef.current = false;
    processingRef.current = false;
    audioRef.current = null;
    if (runningRef.current) scheduleListening(650);
  }

  function speak(reply: VoiceReply) {
    speakingRef.current = true;
    recognitionRef.current?.abort();
    audioRef.current?.pause();
    const audio = new Audio(`/audio/ruth-generative/${reply.id}.mp3`);
    audio.preload = "auto";
    audioRef.current = audio;
    audio.onplay = () => {
      setStatus("Stall the Scam is talking…");
      if (!firstAgentReplyTrackedRef.current) {
        firstAgentReplyTrackedRef.current = true;
        window.stallAnalytics?.track("first_agent_reply");
      }
    };
    audio.onended = finishSpeaking;
    audio.onerror = () => {
      if (!window.speechSynthesis || typeof window.SpeechSynthesisUtterance === "undefined") {
        setStatus("The voice clip could not load. Trying the microphone again…");
        finishSpeaking();
        return;
      }
      const utterance = new SpeechSynthesisUtterance(reply.text);
      utterance.rate = 0.94;
      utterance.pitch = 1;
      utterance.onend = finishSpeaking;
      utterance.onerror = finishSpeaking;
      window.speechSynthesis.speak(utterance);
    };
    void audio.play().catch(() => audio.onerror?.(new Event("error")));
  }

  function handleCaller(text: string) {
    if (!runningRef.current) return;
    processingRef.current = true;
    setInterim("");
    setTranscript((lines) => [...lines, { role: "caller", text }]);
    setStatus("Thinking of another excuse…");
    const response = replyFor(text, turnsRef.current);

    window.setTimeout(() => {
      if (!runningRef.current) return;
      setTranscript((lines) => [...lines, { role: "agent", text: response.text }]);
      setTurns((value) => value + 1);
      speak(response);
    }, 650);
  }

  async function holdMicrophoneOpen() {
    if (microphoneStreamRef.current || !navigator.mediaDevices?.getUserMedia) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      if (!runningRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      microphoneStreamRef.current = stream;
    } catch {
      // SpeechRecognition reports the actionable permission error.
    }
  }

  function startSession() {
    if (running) return;
    if (!voiceSupported) {
      setStatus("Live voice is unavailable here. Open this page in Chrome, or try the text simulation.");
      return;
    }
    setRunning(true);
    runningRef.current = true;
    processingRef.current = true;
    speakingRef.current = false;
    recognitionActiveRef.current = false;
    microphoneBlockedRef.current = false;
    setSeconds(0);
    setTurns(0);
    turnsRef.current = 0;
    firstAgentReplyTrackedRef.current = false;
    window.stallAnalytics?.track("demo_start");
    void holdMicrophoneOpen();
    const opening = chooseReply("opening", Math.floor(Math.random() * voiceReplies.opening.length));
    setTranscript([{ role: "agent", text: opening.text }]);
    window.setTimeout(() => speak(opening), 100);
  }

  function stopSession() {
    runningRef.current = false;
    processingRef.current = false;
    speakingRef.current = false;
    recognitionActiveRef.current = false;
    setRunning(false);
    if (restartTimerRef.current !== null) {
      window.clearTimeout(restartTimerRef.current);
      restartTimerRef.current = null;
    }
    recognitionRef.current?.abort();
    audioRef.current?.pause();
    audioRef.current = null;
    microphoneStreamRef.current?.getTracks().forEach((track) => track.stop());
    microphoneStreamRef.current = null;
    window.speechSynthesis?.cancel();
    setInterim("");
    setStatus(seconds > 0 ? `Session ended after ${formatTime(seconds)}` : "Ready on this device");
  }

  function simulateCaller() {
    if (!voiceSupported) {
      window.stallAnalytics?.track("demo_start");
      const prompt = callerPrompts[turnsRef.current % callerPrompts.length];
      const response = replyFor(prompt, turnsRef.current);
      setTranscript([
        { role: "caller", text: prompt },
        { role: "agent", text: response.text },
      ]);
      setTurns((value) => value + 1);
      setStatus("Text preview complete. Open this page in Chrome for live voice.");
      return;
    }
    if (!runningRef.current) startSession();
    const prompt = callerPrompts[turnsRef.current % callerPrompts.length];
    window.setTimeout(() => handleCaller(prompt), runningRef.current ? 0 : 1800);
  }

  async function startCheckout(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!email.trim()) return;
    setCheckoutBusy(true);
    setBillingMessage("");
    window.stallAnalytics?.track("checkout_start");
    try {
      const response = await fetch(`${apiBase}/api/billing/checkout`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          session_id: window.stallAnalytics?.sessionId ?? "",
          page_path: window.location.pathname,
          ...(window.stallAnalytics?.attribution ?? {}),
        }),
      });
      const data = await response.json() as { checkout_url?: string; detail?: string };
      if (!response.ok || !data.checkout_url) throw new Error(data.detail || "Checkout is unavailable.");
      window.location.assign(data.checkout_url);
    } catch (error) {
      setBillingMessage(error instanceof Error ? error.message : "Checkout is unavailable.");
      setCheckoutBusy(false);
    }
  }

  async function openPortal() {
    setCheckoutBusy(true);
    setBillingMessage("");
    try {
      const response = await fetch(`${apiBase}/api/billing/portal`, { method: "POST", credentials: "include" });
      const data = await response.json() as { portal_url?: string; detail?: string };
      if (!response.ok || !data.portal_url) throw new Error(data.detail || "Billing management is unavailable.");
      window.location.assign(data.portal_url);
    } catch (error) {
      setBillingMessage(error instanceof Error ? error.message : "Billing management is unavailable.");
      setCheckoutBusy(false);
    }
  }

  async function requestAccessCode(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!email.trim()) return;
    setCheckoutBusy(true);
    setBillingMessage("");
    try {
      const response = await fetch(`${apiBase}/api/billing/access-code`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await response.json() as { message?: string; detail?: string };
      if (!response.ok) throw new Error(data.detail || "Access recovery is unavailable.");
      setRestoreStep("verify");
      setBillingMessage(data.message || "Check your email for a six-digit code.");
    } catch (error) {
      setBillingMessage(error instanceof Error ? error.message : "Access recovery is unavailable.");
    } finally {
      setCheckoutBusy(false);
    }
  }

  async function verifyAccessCode(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!email.trim() || restoreCode.length !== 6) return;
    setCheckoutBusy(true);
    setBillingMessage("");
    try {
      const response = await fetch(`${apiBase}/api/billing/verify-code`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code: restoreCode }),
      });
      const data = await response.json() as Subscription & { detail?: string };
      if (!response.ok || !data.subscribed) throw new Error(data.detail || "That code could not restore access.");
      setSubscription(data);
      setRestoreOpen(false);
      setBillingMessage("Membership restored on this device.");
    } catch (error) {
      setBillingMessage(error instanceof Error ? error.message : "That code could not restore access.");
    } finally {
      setCheckoutBusy(false);
    }
  }

  return (
    <main>
      <nav className="nav shell" aria-label="Primary navigation">
        <a className="brand" href="#top" aria-label="Stall the Scam home">
          <span className="brand-mark" aria-hidden="true">S</span>
          Stall the Scam
        </a>
        <div className="nav-links">
          <a href="#how">How it works</a>
          <a href="/how-to-stop-spam-calls">Guides</a>
          <a href="#pricing">Pricing</a>
        </div>
        <a className="button button-small button-dark" href="#console">Try it free</a>
      </nav>

      <section className="hero shell" id="top">
        <div className="eyebrow"><span /> When blocking fails</div>
        <h1>Spam call got through?<br /><em>Waste their time.</em></h1>
        <p className="hero-copy">
          Put a suspicious call on speaker and let Stall the Scam keep the caller busy with natural, harmless delays. The goal is to make your number such an unproductive lead that it is not worth calling again.
        </p>
        <div className="hero-actions">
          <a className="button button-primary" href="#console">Try it free <span>→</span></a>
          <a className="text-link" href="#how">See how it works</a>
        </div>
        <div className="trust-row" aria-label="Product principles">
          <span>● Works with the phone you have</span>
          <span>● No real personal data</span>
          <span>● No calls made for you</span>
        </div>
      </section>

      <section className="console-section shell" id="console">
        <div className="console-copy">
          <p className="section-kicker">Speaker mode</p>
          <h2>One button. You’re out of the conversation.</h2>
          <p>
            Open Stall the Scam on a laptop or spare phone, put the suspicious call on speaker, and leave the two devices near each other.
          </p>
          <div className="setup-steps">
            <div><b>1</b><span><strong>Answer cautiously</strong>Only use this for an unsolicited call you believe is fraudulent.</span></div>
            <div><b>2</b><span><strong>Turn on speaker</strong>Place this laptop or spare phone beside the call.</span></div>
            <div><b>3</b><span><strong>Tap Start</strong>Stall the Scam listens, delays, and asks harmless questions.</span></div>
          </div>
        </div>

        <div className={`console-card ${running ? "is-live" : ""}`}>
          <div className="console-topbar">
            <span className="device-dot" />
            <span>Stall the Scam on this device</span>
            <span className="local-pill">NO REAL INFO</span>
          </div>
          <div className="session-stats">
            <div><span>Call length</span><strong>{formatTime(seconds)}</strong></div>
            <div><span>Times stalled</span><strong>{turns}</strong></div>
            <div><span>Your info shared</span><strong>NONE</strong></div>
          </div>
          <div className="wave-wrap" aria-hidden="true">
            <div className="pulse-ring ring-one" />
            <div className="pulse-ring ring-two" />
            <div className="voice-orb"><span>{running ? "LIVE" : "READY"}</span></div>
          </div>
          <p className="status" aria-live="polite">{status}</p>
          {interim && <p className="interim">“{interim}”</p>}
          <div className="console-actions">
            <button className={`button ${running ? "button-stop" : "button-primary"}`} onClick={running ? stopSession : startSession} disabled={!voiceSupported && !running}>
              {running ? "End session" : voiceSupported ? "Start the stall" : "Chrome required"}
            </button>
            <button className="button button-ghost" onClick={simulateCaller}>Simulate caller line</button>
          </div>
          <div className="transcript" aria-label="Live transcript">
            {transcript.slice(-4).map((line, index) => (
              <div className={`transcript-line ${line.role}`} key={`${line.role}-${index}-${line.text}`}>
                <span>{line.role === "agent" ? "STALL" : "CALLER"}</span>
                <p>{line.text}</p>
              </div>
            ))}
          </div>
          <p className="prototype-note">
            {voiceSupported
              ? "Chrome offers the best microphone support. The mic stays active only during a session. The free tryout stops after one minute."
              : "Live voice is unavailable in this browser. Open the page in Chrome, or use the text simulation to preview a response."}
          </p>
        </div>
      </section>

      <section className="cost-section" id="how">
        <div className="shell">
          <div className="section-heading">
            <p className="section-kicker">Why it works</p>
            <h2>Waste scammer time<br />without wasting yours.</h2>
          </div>
          <div className="cost-grid">
            <article>
              <span className="cost-number">01</span>
              <h3>Keep your distance</h3>
              <p>You decide which suspicious incoming calls to hand off. Stall the Scam never places calls or asks you to call anyone back.</p>
            </article>
            <article>
              <span className="cost-number">02</span>
              <h3>Keep them working</h3>
              <p>Natural prerecorded voices, repeated questions, misplaced glasses, and stubborn software updates turn a rushed pitch into unproductive waiting.</p>
            </article>
            <article>
              <span className="cost-number">03</span>
              <h3>Give nothing away</h3>
              <p>The responses never use your name, bank, passwords, codes, card numbers, or other personal details.</p>
            </article>
          </div>
          <div className="economics-strip">
            <div><span>YOU</span><strong>Tap one button</strong><small>Step away from the pressure</small></div>
            <div className="economics-arrow">→</div>
            <div><span>STALL THE SCAM</span><strong>Keeps them talking</strong><small>Harmless confusion, on repeat</small></div>
            <div className="economics-arrow">→</div>
            <div><span>THE GOAL</span><strong>Become a bad lead</strong><small>Make another call feel pointless</small></div>
          </div>
        </div>
      </section>

      <section className="principles shell">
        <div>
          <p className="section-kicker">The objective</p>
          <h2>Waste their time.<br />Become a bad lead.</h2>
        </div>
        <div className="principle-list">
          <div><span>↑</span><p><strong>Maximize believable delay</strong>Slow pacing, harmless confusion, repeated clarification, and plausible device trouble.</p></div>
          <div><span>↻</span><p><strong>Become a bad lead</strong>The goal is to make your number feel unproductive. No service can promise a scammer will remove it from a list.</p></div>
          <div><span>×</span><p><strong>Never help the scam</strong>No real identities, account details, codes, payments, downloads, threats, or outbound callbacks.</p></div>
        </div>
      </section>

      <section className="pricing-section" id="pricing">
        <div className="shell pricing-wrap">
          <div className="pricing-copy">
            <p className="section-kicker">Consumer membership</p>
            <h2>One simple plan.</h2>
            <p>No special phone number, complicated setup, or usage tokens. Use it from a laptop or spare phone.</p>
          </div>
          <div className="price-card">
            <div className="price-row"><span>$4.99</span><small>/ month</small></div>
            <p>Founding member price</p>
            <ul>
              <li>Unlimited scam-call sessions</li>
              <li>Fresh excuses and delay tactics</li>
              <li>Use on a laptop or spare phone</li>
              <li>Secure billing through Stripe</li>
              <li>Cancel anytime</li>
            </ul>
            {subscription.subscribed ? (
              <div className="subscriber-state">
                <span className="subscriber-pill">{subscription.status === "trialing" ? "Trial active" : "Subscription active"}</span>
                <p className="signup-message">{subscription.email ? `Billing email: ${subscription.email}.` : "Unlimited sessions are ready on this device."}</p>
                <button className="button button-primary" type="button" onClick={openPortal} disabled={checkoutBusy}>Manage subscription</button>
              </div>
            ) : (
              <>
                <form onSubmit={startCheckout}>
                  <label htmlFor="email">Start with secure Stripe checkout</label>
                  <div className="email-row">
                    <input id="email" type="email" placeholder="you@example.com" value={email} onChange={(event) => setEmail(event.target.value)} required disabled={checkoutBusy} />
                    <button className="button button-primary" type="submit" disabled={checkoutBusy}>{checkoutBusy ? "Opening…" : "Start membership"}</button>
                  </div>
                </form>
                <div className="restore-access">
                  <button
                    className="restore-toggle"
                    type="button"
                    onClick={() => {
                      setRestoreOpen((value) => !value);
                      setRestoreStep("request");
                      setRestoreCode("");
                      setBillingMessage("");
                    }}
                  >
                    Already a member? Restore access
                  </button>
                  {restoreOpen && restoreStep === "request" && (
                    <form className="restore-panel" onSubmit={requestAccessCode}>
                      <label htmlFor="restore-email">Use the email from your membership</label>
                      <div className="email-row">
                        <input id="restore-email" type="email" placeholder="you@example.com" value={email} onChange={(event) => setEmail(event.target.value)} required disabled={checkoutBusy} />
                        <button className="button button-dark" type="submit" disabled={checkoutBusy}>{checkoutBusy ? "Sending…" : "Email a code"}</button>
                      </div>
                    </form>
                  )}
                  {restoreOpen && restoreStep === "verify" && (
                    <form className="restore-panel" onSubmit={verifyAccessCode}>
                      <label htmlFor="restore-code">Enter the six-digit code sent to {email}</label>
                      <div className="email-row">
                        <input id="restore-code" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} placeholder="123456" value={restoreCode} onChange={(event) => setRestoreCode(event.target.value.replace(/\D/g, "").slice(0, 6))} required disabled={checkoutBusy} />
                        <button className="button button-dark" type="submit" disabled={checkoutBusy}>{checkoutBusy ? "Checking…" : "Restore access"}</button>
                      </div>
                    </form>
                  )}
                </div>
              </>
            )}
            {billingMessage && <p className={subscription.subscribed ? "signup-message" : "checkout-error"} aria-live="polite">{billingMessage}</p>}
          </div>
        </div>
      </section>

      <section className="faq shell" id="faq">
        <div>
          <p className="section-kicker">Good to know</p>
          <h2>Questions before<br />you press Start.</h2>
        </div>
        <div className="faq-list">
          <details>
            <summary>Does Stall the Scam record my call?</summary>
            <p>No. The current version does not save audio or transcripts. It only needs microphone access while a session is running.</p>
          </details>
          <details>
            <summary>What usage information is collected?</summary>
            <p>We collect first-party product events such as page visits, demo starts, checkout starts, purchases, renewals, and cancellations. Campaign tags are retained for attribution. Audio, transcripts, and the content of calls are not collected.</p>
          </details>
          <details>
            <summary>Will it call scammers for me?</summary>
            <p>No. You choose an incoming call you believe is fraudulent, put it on speaker, and start the conversation. Stall the Scam never originates or returns calls.</p>
          </details>
          <details>
            <summary>Will this stop every scam call?</summary>
            <p>No. Blocking and filtering are the first line of defense. The aim here is only to keep one suspected scammer busy; it cannot promise fewer future calls or removal from a list.</p>
          </details>
          <details>
            <summary>What if I am not sure the call is a scam?</summary>
            <p>Hang up. Find the organization’s official number on its website, statement, or the back of your card, then call that number yourself.</p>
          </details>
        </div>
      </section>

      <section className="safety shell">
        <span>IMPORTANT</span>
        <p>Stall the Scam is for suspicious unsolicited calls, not legitimate businesses, personal disputes, debt collectors, or emergency services. Laws governing automated voices and call monitoring vary by location. The current version does not save audio or transcripts.</p>
      </section>

      <footer className="footer shell">
        <a className="brand" href="#top"><span className="brand-mark">S</span>Stall the Scam</a>
        <p><a href="/how-to-stop-spam-calls">Stop spam calls</a> · <a href="/best-spam-call-blockers">Compare blockers</a> · <a href="/scambaiting-safely">Safety guide</a></p>
        <span>Consumer speaker-mode scam defense · 2026</span>
      </footer>
    </main>
  );
}
